import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { Shield, Network, Camera, KeyRound, ShieldCheck, Cable, Building2, Bell, Eye, Video, Home, Tv2, TrendingDown, CheckCircle, ArrowRight } from 'lucide-react';
import { useT } from '../context/LanguageContext';
import { ui, servicesData } from '../translations';

const ICON_MAP = { Shield, Network, Camera, KeyRound, ShieldCheck, Cable, Building2, Bell, Eye, Video, Home, Tv2, TrendingDown };

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); } }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

const ServicesPage = () => {
  const t = useT();
  const lang = t({ ro: 'ro', en: 'en' });
  useSEO({
    title: lang === 'ro'
      ? 'Servicii IT & Securitate | CCTV, Smart Home, BMS, Rețelistică | Bee-Advised'
      : 'IT & Security Services | CCTV, Smart Home, BMS, Networking | Bee-Advised',
    description: lang === 'ro'
      ? 'Servicii complete: securitate cibernetică, CCTV, control acces, rețelistică SMB, cablare structurată, sisteme BMS, smart home, home cinema și eficientizare costuri.'
      : 'Complete services: cybersecurity, CCTV, access control, SMB networking, structured cabling, BMS, smart home, home cinema and cost optimization.',
    canonical: 'https://www.bee-advised.ro/servicii',
    lang,
  });
  const [activeCategory, setActiveCategory] = useState('all');
  const gridRef = useReveal();

  const categories = [
    { key: 'all', label: t(ui.servicesPage.catAll) },
    { key: 'security', label: t(ui.servicesPage.catSecurity) },
    { key: 'surveillance', label: t(ui.servicesPage.catSurveillance) },
    { key: 'infrastructure', label: t(ui.servicesPage.catInfra) },
    { key: 'smart', label: t(ui.servicesPage.catSmart) },
  ];

  const filtered = activeCategory === 'all' ? servicesData : servicesData.filter(s => s.category === activeCategory);

  return (
    <div>
      {/* PAGE HERO */}
      <section data-testid="services-hero" className="relative bg-[#0A192F] hex-pattern pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-badge"><span className="w-8 h-px bg-[#00BCD4]"/>{t(ui.servicesPage.badge)}</div>
          <h1 className="text-5xl md:text-6xl font-black text-white font-heading tracking-tight leading-none mt-3 max-w-3xl">
            {t(ui.servicesPage.title)}
          </h1>
          <p className="text-slate-400 mt-4 text-lg max-w-2xl leading-relaxed">{t(ui.servicesPage.subtitle)}</p>
        </div>
      </section>

      {/* FILTER TABS */}
      <section className="bg-white border-b border-slate-100 sticky top-[72px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-3">
            {categories.map(c => (
              <button key={c.key} data-testid={`filter-${c.key}`}
                onClick={() => setActiveCategory(c.key)}
                className={`px-5 py-2 text-sm font-semibold uppercase tracking-wider whitespace-nowrap transition-all duration-200 ${activeCategory === c.key ? 'bg-[#0A192F] text-[#00BCD4]' : 'text-slate-500 hover:text-[#0A192F] hover:bg-slate-50'}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={gridRef} className="reveal grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(service => {
              const Icon = ICON_MAP[service.icon] || Shield;
              return (
                <div key={service.id} data-testid={`service-full-card-${service.id}`}
                  className="card-service group flex flex-col">
                  <div className="relative h-44 -mx-8 -mt-8 mb-6 overflow-hidden">
                    <img src={service.image} alt={t(service.name)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"/>
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0A192F]/60"/>
                    <div className="absolute bottom-4 left-4 icon-container bg-[#0A192F]/80 border-[#00BCD4]/40">
                      <Icon size={20} className="text-[#00BCD4]" strokeWidth={1.5}/>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-[#0A192F] font-heading mb-2">{t(service.name)}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-5 flex-1">{t(service.desc)}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle size={14} className="text-[#00BCD4] shrink-0"/>
                        {t(f)}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" data-testid={`service-cta-${service.id}`}
                    className="btn-secondary text-sm mt-auto justify-center">
                    {t(ui.servicesPage.cta)} <ArrowRight size={14}/>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A192F] hex-pattern">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white font-heading">{t(ui.cta.title)}</h2>
          <p className="text-slate-400 mt-4 text-lg">{t(ui.cta.subtitle)}</p>
          <div className="flex flex-wrap gap-4 mt-8 justify-center">
            <Link to="/contact" className="btn-primary">{t(ui.cta.btn1)} <ArrowRight size={16}/></Link>
            <a href="tel:+40744324272" className="btn-primary-outline">{t(ui.cta.btn2)}</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
